import React, { useCallback } from 'react'
import IconButton from '@mui/material/IconButton'
import { useAppSelector } from '../../redux/hooks'
import { IcMinus, IcPlus } from '../common/Icons'
import clsx from 'clsx'

interface Props {
  index: number
  type: GuestType
  hasBorderBot?: boolean
  personCapacity: number
  allowZeroAdult?: boolean
  maxPet?: number | null
  guestCount: any
  onChangeGuestInfo?: (_: any) => void
}

export type GuestType = 'adult' | 'child' | 'infant' | 'pet'

const SelectGuestItem: React.FC<Props> = ({
  index,
  type,
  hasBorderBot,
  personCapacity,
  allowZeroAdult,
  maxPet,
  guestCount,
  onChangeGuestInfo,
}) => {
  const { checkIn, checkOut } = useAppSelector((state) => state.property)
  const { adults, children, infants, pets } = guestCount

  const renderText = (type: GuestType, labelType: 'title' | 'subTitle') => {
    switch (type) {
      case 'adult':
        return labelType === 'title' ? 'Adults' : 'Ages 13+'
      case 'child':
        return labelType === 'title' ? 'Children' : 'Ages 2-12'
      case 'infant':
        return labelType === 'title' ? 'Infants' : 'Under 2'
      case 'pet':
        return labelType === 'title' ? 'Pets' : ''
    }
  }

  const renderCount = (type: GuestType) => {
    switch (type) {
      case 'adult':
        return adults
      case 'child':
        return children
      case 'infant':
        return infants
      case 'pet':
        return pets
    }
  }

  const handleChangeCount = useCallback(
    (type: GuestType, isIncreasement: boolean) => () => {
      switch (type) {
        case 'adult': {
          let newAdults = isIncreasement ? adults + 1 : adults > 1 ? adults - 1 : 1
          if (allowZeroAdult) {
            newAdults = isIncreasement ? adults + 1 : adults > 0 ? adults - 1 : 0
          }
          if (typeof onChangeGuestInfo === 'function') onChangeGuestInfo({ adults: newAdults, children, infants, pets })
          break
        }
        case 'child': {
          const newChild = isIncreasement ? children + 1 : children > 0 ? children - 1 : 0

          if (typeof onChangeGuestInfo === 'function') onChangeGuestInfo({ adults, children: newChild, infants, pets })
          break
        }
        case 'infant': {
          const newInfants = isIncreasement ? infants + 1 : infants > 0 ? infants - 1 : 0
          if (typeof onChangeGuestInfo === 'function')
            onChangeGuestInfo({ adults, children, infants: newInfants, pets })
          break
        }
        case 'pet': {
          let newPets = isIncreasement ? pets + 1 : pets > 0 ? pets - 1 : 0
          newPets = newPets > 5 ? 5 : newPets // max 5 pets
          if (typeof onChangeGuestInfo === 'function') onChangeGuestInfo({ adults, children, infants, pets: newPets })
          break
        }
      }
    },
    [adults, children, infants, pets, checkIn, checkOut]
  )

  const disabledAdult = (type === 'adult' || type === 'child') && Number(adults) + Number(children) >= personCapacity
  const isDisabledPlus = disabledAdult || (type === 'pet' && !maxPet) || (type === 'pet' && pets >= 5)
  return (
    <div
      className={clsx('dtravel-flex-between items-center', {
        'border-b border-solid border-neutral-300': hasBorderBot,
        'pt-[16px]': index !== 0,
        'pb-[16px]': index !== 3,
      })}
    >
      <div>
        <p className={'dtravel-inter-400 dtravel-neutral-800 dtravel-text-14-20'}>{renderText(type, 'title')}</p>
        {/*<p className={'dtravel-inter-400 dtravel-neutral-600 dtravel-text-14-18'}>{renderText(type, 'subTitle')}</p>*/}
      </div>
      <div className={'dtravel-flex-between items-center gap-2'}>
        <IconButton
          onClick={handleChangeCount(type, false)}
          disabled={type === 'pet' && !maxPet}
          className={'cursor-pointer disabled:cursor-not-allowed disabled:opacity-30'}
          size={'small'}
        >
          <IcMinus width={36} height={36} />
        </IconButton>
        <span
          className={
            'dtravel-inter-400 dtravel-neutral-900 dtravel-text-20-28 min-w-[48px] flex items-center justify-center'
          }
        >
          {renderCount(type)}
        </span>
        <IconButton
          onClick={handleChangeCount(type, true)}
          disabled={isDisabledPlus}
          className={'cursor-pointer disabled:cursor-not-allowed disabled:opacity-30'}
          size={'small'}
        >
          <IcPlus width={36} height={36} />
        </IconButton>
      </div>
    </div>
  )
}

export default SelectGuestItem
